import axios from "axios";
import { idb } from "@/lib/offlineDb";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });

export const tokens = {
  get: () => localStorage.getItem("lp_access"),
  set: (d) => {
    if (d.access_token) localStorage.setItem("lp_access", d.access_token);
    if (d.refresh_token) localStorage.setItem("lp_refresh", d.refresh_token);
  },
  clear: () => {
    localStorage.removeItem("lp_access");
    localStorage.removeItem("lp_refresh");
  },
};

api.interceptors.request.use((cfg) => {
  const t = tokens.get();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export const isNetworkError = (e) => !e?.response;
const cacheKey = (cfg) => `GET ${cfg.url}`;
const cacheable = (cfg) => cfg?.method === "get" && cfg.responseType !== "blob";

api.interceptors.response.use(
  (r) => {
    if (cacheable(r.config)) idb.set("meta", cacheKey(r.config), r.data).catch(() => {});
    return r;
  },
  async (err) => {
    const orig = err.config;
    if (isNetworkError(err) && cacheable(orig)) {
      const cached = await idb.get("meta", cacheKey(orig)).catch(() => null);
      if (cached !== undefined && cached !== null) return { data: cached, status: 200, config: orig, cached: true };
    }
    if (err.response?.status === 401 && !orig._retry && !orig.url.includes("/auth/")) {
      orig._retry = true;
      try {
        const { data } = await api.post("/auth/refresh", { refresh_token: localStorage.getItem("lp_refresh") });
        tokens.set(data);
        return api(orig);
      } catch (e) {
        tokens.clear();
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const errMsg = (e) =>
  isNetworkError(e) ? "You're offline. This needs an internet connection." : formatApiErrorDetail(e.response?.data?.detail) || e.message;

let curriculumCache = null;
export async function getCurriculum() {
  if (!curriculumCache) curriculumCache = (await api.get("/curriculum")).data;
  return curriculumCache;
}

export async function clearOfflineData() {
  await Promise.all(["meta", "lessons", "quizzes", "queue"].map((s) => idb.clear(s).catch(() => {})));
}

export async function streamTutor(language, message, onDelta) {
  const send = () =>
    fetch(`${API}/tutor/${language}/stream`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tokens.get()}` },
      body: JSON.stringify({ message }),
    });
  let res = await send();
  if (res.status === 401) {
    const { data } = await api.post("/auth/refresh", { refresh_token: localStorage.getItem("lp_refresh") });
    tokens.set(data);
    res = await send();
  }
  if (!res.ok) throw new Error("Lumi couldn't reply just now. Please try again.");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      const ev = JSON.parse(p.slice(6));
      if (ev.error) throw new Error(ev.error);
      if (ev.delta) onDelta(ev.delta);
    }
  }
}
